/**
 * Responsibility: say which game is being scanned, so the reader can use
 * its geometry.
 *
 * **This is the cheapest accuracy there is.** Knowing the game means
 * knowing where that game prints its name and its collector number, and
 * cropping to those two places recovers far more text than reading the
 * whole card ever does. Detecting the game from the picture is a real
 * problem; being told it is free, and whoever is holding the card
 * already knows.
 *
 * **"Not sure" is a first-class answer, not a failure to choose.** It
 * reads the card whole, which is what every scan did before this
 * existed. Someone emptying a mixed shoebox should not have to lie.
 *
 * Only games whose layout has actually been measured are offered. An
 * option here is a promise that the crops are right for that game, so
 * the list grows when a layout is verified rather than when a game is
 * added to the catalogue.
 */
import { ToggleButton, ToggleButtonGroup, Tooltip, Typography, Stack } from '@mui/material';

/** A game the reader has verified geometry for, or nothing. */
export type ScanGame = 'pokemon' | 'magic' | 'yugioh' | '';

const CHOICES: { value: ScanGame; label: string; hint: string }[] = [
  { value: 'pokemon', label: 'Pokemon', hint: 'Name top left, number in a bottom corner.' },
  { value: 'magic', label: 'Magic', hint: 'Name across the title bar, collector line bottom left.' },
  { value: 'yugioh', label: 'Yu-Gi-Oh', hint: 'Name top left, set code along the bottom edge.' },
  { value: '', label: 'Not sure', hint: 'Reads the whole card. Slower and less accurate.' },
];

export function GamePicker({
  game,
  onChange,
  disabled = false,
}: {
  game: ScanGame;
  onChange: (next: ScanGame) => void;
  disabled?: boolean;
}) {
  return (
    <Stack spacing={0.5}>
      <Typography variant="caption" color="text.secondary">
        What are you scanning?
      </Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={game}
        disabled={disabled}
        // `null` arrives when the pressed button was already selected.
        // Clearing the choice would silently drop the person back to
        // reading the whole card, so the current one is kept instead.
        onChange={(_event, next: ScanGame | null) => {
          if (next !== null) onChange(next);
        }}
        aria-label="Which game the card is from"
      >
        {CHOICES.map((choice) => (
          <Tooltip key={choice.label} title={choice.hint}>
            <ToggleButton value={choice.value} aria-label={choice.label}>
              {choice.label}
            </ToggleButton>
          </Tooltip>
        ))}
      </ToggleButtonGroup>
    </Stack>
  );
}
